import type { HttpError, OpenNotificationParams } from "@refinedev/core";

/**
 * Account deletion is a background job on the server (immersive-server,
 * plans/compliance-launch/v2-reports-blocking-deletion.md §7): `DELETE user/:id`
 * schedules it and answers 202 `{ status }`, and the job works through its
 * steps, retried by a sweep. A user row carries its open job as `deletion`;
 * `GET admin/account-deletions` lists every job, including the ones whose
 * account is already gone.
 */
export const ACCOUNT_DELETIONS_RESOURCE = "admin/account-deletions";

export type AccountDeletionStatus = "pending" | "waiting_hold" | "running" | "done" | "failed";

/** The open (not done) job on a `GET user` / `GET user/:id` row; null when there is none. */
export type UserDeletion = {
  status: Exclude<AccountDeletionStatus, "done">;
  requestedBy: "self" | "admin";
  requestedAt: string;
  attempts: number;
  lastError: string | null;
};

/** One row of `GET admin/account-deletions`. No FK: `userId` may name an account that no longer exists. */
export type AccountDeletionRecord = {
  id: number;
  userId: number;
  requestedBy: "self" | "admin";
  requestedAt: string;
  status: AccountDeletionStatus;
  /** step → ISO time it finished. */
  steps: Record<string, string>;
  attempts: number;
  lastError: string | null;
  completedAt: string | null;
};

export const ACCOUNT_DELETION_STATUSES: readonly AccountDeletionStatus[] = [
  "pending",
  "waiting_hold",
  "running",
  "done",
  "failed",
];

export const DELETION_STATUS_LABEL: Record<AccountDeletionStatus, string> = {
  pending: "Deletion scheduled",
  waiting_hold: "Waiting on a hold",
  running: "Running",
  done: "Done",
  failed: "Deletion failed",
};

export const DELETION_STATUS_COLOR: Record<AccountDeletionStatus, "warning" | "info" | "success" | "error"> = {
  pending: "info",
  waiting_hold: "warning",
  running: "info",
  done: "success",
  failed: "error",
};

/** The one line a user row shows for its open deletion. */
export const deletionLabel = (deletion: Pick<UserDeletion, "status" | "lastError">): string =>
  deletion.status === "failed" && deletion.lastError
    ? `${DELETION_STATUS_LABEL.failed}: ${deletion.lastError}`
    : DELETION_STATUS_LABEL[deletion.status];

/**
 * The delete button's notifications, on the list, the detail and the edit
 * page alike. Success names what the 202 said — the deletion is scheduled, or
 * waits on an evidence hold — never "deleted": the account is still there
 * until the job finishes. A server from before the job answered 204 with no
 * body, and that delete did happen inline.
 *
 * The snackbar's headline is `description`; `message` is the line under it.
 */
export const userDeleteNotifications = {
  successNotification: (data?: unknown): OpenNotificationParams => {
    const status = (data as { data?: { status?: unknown } } | undefined)?.data?.status;
    if (status === "waiting_hold") {
      return {
        type: "success",
        description: "Deletion scheduled, waiting on a hold",
        message: "An evidence hold names this account. The deletion runs once the hold is released.",
      };
    }
    if (status === "scheduled") {
      return {
        type: "success",
        description: "Deletion scheduled",
        message: "The account's data is removed in the background. Account Deletions shows its progress.",
      };
    }
    return { type: "success", description: "User deleted", message: "The account is gone." };
  },
  // A refused delete (an account that still owns v1 content) carries the
  // server's reason, not just the status code.
  errorNotification: (error?: unknown): OpenNotificationParams => {
    // The data provider rejects with Refine's HttpError; the hook types it loosely.
    const failure = error as HttpError | undefined;
    return {
      type: "error",
      message: failure?.message ?? "Could not delete this user.",
      description: failure?.statusCode === 409 ? "Nothing was deleted." : undefined,
    };
  },
};
